/**
 * Owner self-service sub-delegate management page — Feature 075 (plan 013,
 * Workstream C). Route: /workspace/sub-delegates (protected, AppLayout).
 *
 * Lists active + pending sub-delegates, supports invite / resend / cancel /
 * revoke / edit-permissions against the owner endpoints. Hidden from
 * SUB_DELEGATE users in the nav; a direct visit surfaces the backend 403 as
 * a friendly "not available" state.
 */

import { useState } from 'react';
import { AlertCircle, ShieldOff, UserPlus, Users } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Separator } from '@/shared/components/ui/separator';
import { SeatUsagePill } from '@/shared/components/sub-delegation';
import { useToast } from '@/shared/components/Toast';
import {
  useCancelSubDelegateInvite,
  useInviteSubDelegate,
  useOwnerSubDelegates,
  useResendSubDelegateInvite,
  useRevokeSubDelegate,
  useUpdateSubDelegatePermissions,
} from '../../hooks/useSubDelegateManagement';
import type { InviteDelegatePayload } from '../../types/subDelegateManagement';
import { DelegateCard } from './DelegateCard';
import { InviteDelegateDialog } from './InviteDelegateDialog';

const errorMessageFrom = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Something went wrong. Please try again.';
};

const statusCodeFrom = (error: unknown): number | undefined =>
  error && typeof error === 'object' && 'statusCode' in error
    ? (error as { statusCode?: number }).statusCode
    : undefined;

export function SubDelegateManagementPage() {
  const { showToast } = useToast();
  const list = useOwnerSubDelegates();
  const invite = useInviteSubDelegate();
  const resend = useResendSubDelegateInvite();
  const cancel = useCancelSubDelegateInvite();
  const revoke = useRevokeSubDelegate();
  const updatePermissions = useUpdateSubDelegatePermissions();

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const handleInvite = async (payload: InviteDelegatePayload): Promise<boolean> => {
    setInviteError(null);
    try {
      await invite.mutateAsync(payload);
      showToast(`Invitation sent to ${payload.email}`, 'success');
      return true;
    } catch (error) {
      setInviteError(errorMessageFrom(error));
      return false;
    }
  };

  const mutateWithToast = (action: Promise<unknown>, successMessage: string) => {
    action
      .then(() => showToast(successMessage, 'success'))
      .catch((error) => showToast(errorMessageFrom(error), 'error'));
  };

  const isAnyMutating = resend.isPending || cancel.isPending || revoke.isPending;

  const data = list.data;
  const usedCount = (data?.meta.active ?? 0) + (data?.meta.pending ?? 0);
  const effectiveLimit = data?.meta.effectiveLimit ?? 3;
  const atLimit = !!data && usedCount >= effectiveLimit;
  const isForbidden = list.isError && statusCodeFrom(list.error) === 403;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#026857]/10 text-[#026857]">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Sub-delegates</h1>
              <p className="text-sm text-gray-500">
                People who can act in your workspace on your behalf.
              </p>
            </div>
          </div>
          {data && !isForbidden && (
            <div className="flex items-center gap-3">
              <SeatUsagePill
                used={usedCount}
                limit={effectiveLimit}
                isDefaultLimit={data.meta.limit === null}
              />
              <button
                type="button"
                onClick={() => {
                  setInviteError(null);
                  setIsInviteOpen(true);
                }}
                disabled={atLimit}
                title={atLimit ? 'All sub-delegate seats are in use' : undefined}
                className="flex items-center gap-1.5 rounded-md bg-[#026857] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#025246] disabled:opacity-50"
              >
                <UserPlus className="h-4 w-4" />
                Invite sub-delegate
              </button>
            </div>
          )}
        </div>

        {list.isLoading && (
          <div className="space-y-3" data-testid="loading-state">
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        )}

        {isForbidden && (
          <div
            className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white px-6 py-12 text-center"
            data-testid="forbidden-state"
          >
            <ShieldOff className="h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-900">
              Not available for sub-delegate accounts
            </p>
            <p className="max-w-sm text-xs text-gray-500">
              Sub-delegates cannot invite or manage other sub-delegates. Only
              the workspace owner can manage this list.
            </p>
          </div>
        )}

        {list.isError && !isForbidden && (
          <div
            className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
            data-testid="error-state"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessageFrom(list.error)}</span>
          </div>
        )}

        {data && (
          <>
            {atLimit && (
              <div
                className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                data-testid="limit-reached-callout"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  All sub-delegate seats are in use. Revoke a delegate or
                  cancel a pending invitation to free up a seat.
                </span>
              </div>
            )}

            {usedCount === 0 ? (
              <div
                className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center"
                data-testid="empty-state"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#026857]/10 text-[#026857]">
                  <UserPlus className="h-6 w-6" />
                </span>
                <p className="text-sm font-medium text-gray-900">
                  No sub-delegates yet
                </p>
                <p className="max-w-sm text-xs text-gray-500">
                  Invite a trusted person to help manage your workspace —
                  messaging, clients, profile and more, with the permissions
                  you choose.
                </p>
                <button
                  type="button"
                  onClick={() => setIsInviteOpen(true)}
                  className="mt-1 rounded-md bg-[#026857] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#025246]"
                >
                  Invite your first sub-delegate
                </button>
              </div>
            ) : (
              <>
                <section aria-label="Active sub-delegates" className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Active ({data.active.length})
                  </h2>
                  {data.active.length === 0 ? (
                    <p className="text-sm text-gray-500" data-testid="empty-active">
                      No active sub-delegates yet.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {data.active.map((delegate) => (
                        <DelegateCard
                          key={delegate.id}
                          kind="active"
                          delegate={delegate}
                          isMutating={revoke.isPending}
                          isSavingPermissions={updatePermissions.isPending}
                          onRevoke={(id) =>
                            mutateWithToast(revoke.mutateAsync(id), 'Access revoked')
                          }
                          onUpdatePermissions={(id, permissions) =>
                            mutateWithToast(
                              updatePermissions.mutateAsync({
                                relationshipId: id,
                                permissions,
                              }),
                              'Permissions updated',
                            )
                          }
                        />
                      ))}
                    </ul>
                  )}
                </section>

                <Separator />

                <section aria-label="Pending invitations" className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Pending ({data.pending.length})
                  </h2>
                  {data.pending.length === 0 ? (
                    <p className="text-sm text-gray-500" data-testid="empty-pending">
                      No pending invitations.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {data.pending.map((delegate) => (
                        <DelegateCard
                          key={delegate.id}
                          kind="pending"
                          delegate={delegate}
                          isMutating={isAnyMutating}
                          onResend={(id) =>
                            mutateWithToast(resend.mutateAsync(id), 'Invitation resent')
                          }
                          onCancel={(id) =>
                            mutateWithToast(
                              cancel.mutateAsync(id),
                              'Invitation cancelled',
                            )
                          }
                        />
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </>
        )}

        <InviteDelegateDialog
          open={isInviteOpen}
          onOpenChange={setIsInviteOpen}
          isSubmitting={invite.isPending}
          errorMessage={inviteError}
          onSubmit={handleInvite}
        />
      </div>
    </div>
  );
}
